import type { Component } from "solid-js";
import { useI18n } from "@/api/i18n";
import { cn } from "@/libs/cn";
import type { BillingInterval } from "../api/types";

type BillingIntervalToggleProps = {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
};

/**
 * Segmented Monthly/Yearly pill with a sliding primary highlight, matching the
 * pricing design. Yearly carries an inline savings badge.
 */
export const BillingIntervalToggle: Component<BillingIntervalToggleProps> = (
  props,
) => {
  const { t } = useI18n();

  const isYearly = () => props.value === "yearly";

  return (
    <div class="relative grid min-w-[280px] grid-cols-2 rounded-full border border-border bg-white p-1 shadow-sm">
      <div
        class={cn(
          "absolute top-1 bottom-1 w-[calc(50%-0.25rem)] rounded-full bg-primary shadow-[0_2px_8px_rgba(31,119,235,0.4)] transition-[left] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
          isYearly() ? "left-1/2" : "left-1",
        )}
      />
      <button
        type="button"
        class={cn(
          "relative z-10 rounded-full py-2.5 text-sm font-semibold transition-colors",
          isYearly()
            ? "text-muted-foreground hover:text-foreground"
            : "text-white",
        )}
        onClick={() => props.onChange("monthly")}
      >
        {t("billing.upgrade.interval_toggle.monthly")}
      </button>
      <button
        type="button"
        class={cn(
          "relative z-10 inline-flex items-center justify-center gap-[7px] rounded-full py-2.5 text-sm font-semibold transition-colors",
          isYearly()
            ? "text-white"
            : "text-muted-foreground hover:text-foreground",
        )}
        onClick={() => props.onChange("yearly")}
      >
        {t("billing.upgrade.interval_toggle.yearly")}
        <span
          class={cn(
            "rounded-[5px] px-1.5 py-0.5 text-[10.5px] font-bold transition-colors",
            isYearly()
              ? "bg-white/20 text-white"
              : "bg-primary/10 text-primary",
          )}
        >
          {t("billing.upgrade.interval_toggle.yearly_savings_badge")}
        </span>
      </button>
    </div>
  );
};
